import { useState, useEffect } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import {
  getOrganization,
  getOrganizationStats,
  getMembers,
  getInvites,
  getErrorMessage,
} from '../../api/organizationApi';
import {
  Organization,
  OrganizationStats,
  Member,
  Invite,
  formatLimit,
  getTierColor,
  formatStatus,
} from '../../types/organization';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';

export function OrganizationDetailScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const id = currentScreen.params?.organizationId as string;

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stats' | 'members' | 'invites'>('stats');

  useEffect(() => {
    if (id) loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [org, orgStats, orgMembers, orgInvites] = await Promise.all([
        getOrganization(id),
        getOrganizationStats(id),
        getMembers(id),
        getInvites(id).catch(() => []),
      ]);
      setOrganization(org);
      setStats(orgStats);
      setMembers(orgMembers);
      setInvites(orgInvites);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  if (error || !organization || !stats) {
    return (
      <div className="p-4 space-y-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--danger)' }}>{error || 'Организация не найдена'}</p>
        </Card>
        <Button onClick={() => popScreen()} variant="secondary" fullWidth>&larr; Назад</Button>
      </div>
    );
  }

  const tierColor = getTierColor(organization.subscription_tier);

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '8px 12px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    backgroundColor: isActive ? 'var(--accent)' : 'var(--bg-secondary)',
    color: isActive ? '#FFFFFF' : 'var(--text-hint)',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'center',
  });

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title={organization.name}
        rightAction={
          <button onClick={() => pushScreen('organization-settings', { organizationId: id })} className="text-2xl">
            ⚙️
          </button>
        }
      />

      {/* Header */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex gap-2 mt-2">
              <span className="text-xs px-2 py-1 rounded text-white" style={{ backgroundColor: tierColor }}>
                {stats.limits.tier_name}
              </span>
              <span
                className="text-xs px-2 py-1 rounded text-white"
                style={{ backgroundColor: organization.is_active ? '#10b981' : '#ef4444' }}
              >
                {formatStatus(organization.subscription_status)}
              </span>
            </div>
          </div>
        </div>

        {organization.subscription_expires_at && (
          <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
            <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
              {organization.subscription_status === 'trial' ? 'Триал' : 'Подписка'} истекает:{' '}
              {new Date(organization.subscription_expires_at).toLocaleDateString('ru-RU')}
            </p>
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div className="flex gap-2">
        <button style={tabStyle(activeTab === 'stats')} onClick={() => setActiveTab('stats')}>Статистика</button>
        <button style={tabStyle(activeTab === 'members')} onClick={() => setActiveTab('members')}>Участники ({members.length})</button>
        <button style={tabStyle(activeTab === 'invites')} onClick={() => setActiveTab('invites')}>Приглашения ({invites.filter((i) => i.is_active).length})</button>
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-3">
          {[
            { label: 'Участники', current: stats.members, max: stats.limits.max_members, icon: '👥' },
            { label: 'Спортсмены', current: stats.athletes, max: stats.limits.max_athletes, icon: '🏃' },
            { label: 'Ассистенты', current: stats.assistants, max: stats.limits.max_assistants, icon: '🤖' },
            { label: 'Группы', current: stats.groups, max: stats.limits.max_groups, icon: '👨‍👩‍👧‍👦' },
            { label: 'Тренеры', current: stats.coaches, max: stats.limits.max_coaches, icon: '🎓' },
          ].map((item) => {
            const percentage = item.max === -1 ? 0 : (item.current / item.max) * 100;
            const isNearLimit = percentage >= 80;
            return (
              <Card key={item.label}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{item.icon}</span>
                    <span style={{ color: 'var(--text-hint)' }}>{item.label}</span>
                  </div>
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {item.current} / {formatLimit(item.max)}
                  </span>
                </div>
                {item.max !== -1 && (
                  <div className="w-full h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--bg-primary)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(percentage, 100)}%`,
                        backgroundColor: isNearLimit ? '#ef4444' : tierColor,
                      }}
                    />
                  </div>
                )}
                {isNearLimit && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>Близко к лимиту</p>}
              </Card>
            );
          })}

          <Card>
            <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Ваш тариф</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{stats.limits.tier_name}</p>
                <p className="text-sm" style={{ color: 'var(--text-hint)' }}>AI-запросов: {formatLimit(stats.limits.max_ai_requests)}/мес</p>
              </div>
              {stats.limits.tier_price > 0 && (
                <span className="font-bold" style={{ color: 'var(--accent)' }}>{stats.limits.tier_price} rub/мес</span>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div className="space-y-3">
          <Button onClick={() => pushScreen('organization-settings', { organizationId: id })} variant="secondary" fullWidth>
            Управление участниками
          </Button>
          <div className="space-y-2">
            {members.map((member) => (
              <Card key={member.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{member.role === 'owner' ? '👑' : '⭐'}</span>
                    <span style={{ color: 'var(--text-primary)' }}>
                      {member.user_name || `User ${member.user_id.substring(0, 8)}...`}
                    </span>
                  </div>
                  <span className="text-sm" style={{ color: 'var(--text-hint)' }}>
                    {member.role === 'owner' ? 'Владелец' : 'Админ'}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <div className="space-y-3">
          <Button onClick={() => pushScreen('organization-settings', { organizationId: id })} fullWidth>
            Создать приглашение
          </Button>
          {invites.filter((i) => i.is_active).length === 0 ? (
            <Card>
              <p className="text-center py-4" style={{ color: 'var(--text-hint)' }}>Нет активных приглашений</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {invites.filter((i) => i.is_active).map((invite) => (
                <Card key={invite.id}>
                  <p className="font-mono font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{invite.code}</p>
                  <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                    Использований: {invite.current_uses}/{formatLimit(invite.max_uses)}
                    {' \u00b7 '}
                    Истекает: {new Date(invite.expires_at).toLocaleDateString('ru-RU')}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
